/**
 * §5 text primitives — `plainText`, the two flatteners, the two splicers and their
 * conveniences.
 *
 * These are CONTRACT SURFACE, not implementation details: about eight of the ~22 ops route
 * through them, and one off-by-one makes all eight subtly wrong at once. So there is exactly
 * one implementation of the offsets and exactly one interpretation of the timing rule.
 *
 * THE FROZEN TIMING RULE, implemented once, here:
 *   surviving runs keep their own `time`; a run whose origin is 'asr' | 'file' | 'manual' is
 *   NEVER proportionally rewritten. Only runs CREATED by the splice receive synthesized
 *   timings, marked origin 'interpolated'.
 *
 * Proportional redistribution of MEASURED word times is what makes every karaoke export
 * after a cleaning pass quietly wrong, so this file never does it.
 */

import type {
  Cue,
  FlatCue,
  FlatLine,
  IdAllocator,
  RunId,
  TextEdit,
  TextLine,
  TextRun,
  Ticks,
} from '../../../contracts/cue-model.ts';
import { T } from '../time/ticks.ts';
import { isCjkCodePoint } from '../text/width.ts';

/*───────────────────────────────────────────────────────────────────────────────────────
  plainText — THE canonical rendering, and the ONLY valid basis for equality/dedupe/diff.
───────────────────────────────────────────────────────────────────────────────────────*/

/**
 * FROZEN: kind:'drawing' runs contribute NOTHING. `DrawingSpec.commands` is never text, so
 * `plainText` can never leak path commands into dedupe or diff, and an emptiness check must
 * consult `kind` (see NormalizeOp.dropEmpty).
 */
export function plainText(cue: Cue, lineJoiner = '\n'): string {
  const parts: string[] = [];
  for (const line of cue.lines) parts.push(lineTextOf(line));
  return parts.join(lineJoiner);
}

export function lineTextOf(line: TextLine): string {
  let s = '';
  for (const run of line.runs) if (run.kind !== 'drawing') s += run.text;
  return s;
}

/** True when any run is a vector drawing. A drawing-bearing cue is CONTENT even when its
 *  plainText is '' — every pure \p typeset sign has empty text. */
export function hasAnyDrawing(cue: Cue): boolean {
  for (const line of cue.lines)
    for (const run of line.runs) if (run.kind === 'drawing') return true;
  return false;
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Flatteners
───────────────────────────────────────────────────────────────────────────────────────*/

export function flattenLine(line: TextLine): FlatLine {
  let text = '';
  const runIndex: number[] = [];
  const runOffset: number[] = [];
  for (let r = 0; r < line.runs.length; r++) {
    const run = line.runs[r] as TextRun;
    if (run.kind === 'drawing') continue; // text is '' by contract
    for (let i = 0; i < run.text.length; i++) {
      runIndex.push(r);
      runOffset.push(i);
    }
    text += run.text;
  }
  return { text, runIndex, runOffset };
}

/**
 * THE cue-level offset map. Line breaks are MATERIALISED as '\n' at their break positions,
 * so a pattern spanning a break ('[MAN SPEAKING\nIN SPANISH]') is visible to ONE match.
 *
 * The '\n' belongs to the line it BEGINS. `runIndex` is −1 there, and `runOffset` is −1 too:
 * it is meaningful only where `runIndex >= 0`.
 */
export function flattenCue(cue: Cue): FlatCue {
  const f = flattenCueInternal(cue);
  return { text: f.text, lineIndex: f.lineIndex, runIndex: f.runIndex, runOffset: f.runOffset };
}

interface CueFlatInternal extends FlatCue {
  /** Every run of every line, concatenated, so provenance survives a line merge. */
  readonly runs: readonly TextRun[];
  /** Per output character: index into `runs`, or −1 at a break character. */
  readonly globalRun: readonly number[];
  /** Per global run index: which source line it belongs to. */
  readonly runLine: readonly number[];
}

function flattenCueInternal(cue: Cue): CueFlatInternal {
  let text = '';
  const lineIndex: number[] = [];
  const runIndex: number[] = [];
  const runOffset: number[] = [];
  const globalRun: number[] = [];
  const runs: TextRun[] = [];
  const runLine: number[] = [];

  for (let l = 0; l < cue.lines.length; l++) {
    if (l > 0) {
      text += '\n';
      lineIndex.push(l);
      runIndex.push(-1);
      runOffset.push(-1);
      globalRun.push(-1);
    }
    const line = cue.lines[l] as TextLine;
    for (let r = 0; r < line.runs.length; r++) {
      const run = line.runs[r] as TextRun;
      const gi = runs.length;
      runs.push(run);
      runLine.push(l);
      if (run.kind === 'drawing') continue;
      for (let i = 0; i < run.text.length; i++) {
        lineIndex.push(l);
        runIndex.push(r);
        runOffset.push(i);
        globalRun.push(gi);
      }
      text += run.text;
    }
  }
  return { text, lineIndex, runIndex, runOffset, globalRun, runs, runLine };
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Edit application — one character-provenance model shared by both splicers.
───────────────────────────────────────────────────────────────────────────────────────*/

/** Provenance of one character in the spliced result. `-1` = inserted by the edit. */
const INSERTED = -1;

interface Applied {
  readonly text: string;
  /** Per output character: the INPUT character index it came from, or INSERTED. */
  readonly from: readonly number[];
  readonly changed: boolean;
}

/**
 * Applies edits to a flat string, tracking where every surviving character came from.
 *
 * Edits are sorted by `at` and applied against the ORIGINAL offsets — never cascaded — so a
 * caller that computed all its offsets from one `flattenCue` call gets what it asked for.
 * Overlapping edits resolve first-wins and the loser is DROPPED, which is the only choice
 * that keeps the caller's offsets meaningful; silently shifting it would corrupt the rest.
 */
function applyEdits(source: string, edits: readonly TextEdit[]): Applied {
  if (edits.length === 0)
    return { text: source, from: identityFrom(source.length), changed: false };

  const sorted = [...edits].sort((a, b) => a.at - b.at || a.remove - b.remove);
  const out: string[] = [];
  const from: number[] = [];
  let cursor = 0;
  let changed = false;

  for (const edit of sorted) {
    const at = Math.max(0, Math.min(source.length, Math.trunc(edit.at)));
    const remove = Math.max(0, Math.trunc(edit.remove));
    if (at < cursor) continue; // overlaps an earlier edit — first wins
    for (let i = cursor; i < at; i++) {
      out.push(source[i] as string);
      from.push(i);
    }
    if (edit.insert.length > 0) {
      for (let i = 0; i < edit.insert.length; i++) {
        out.push(edit.insert[i] as string);
        from.push(INSERTED);
      }
      changed = true;
    }
    if (remove > 0 && at < source.length) changed = true;
    cursor = Math.min(source.length, at + remove);
  }
  for (let i = cursor; i < source.length; i++) {
    out.push(source[i] as string);
    from.push(i);
  }
  return { text: out.join(''), from, changed };
}

function identityFrom(n: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < n; i++) out.push(i);
  return out;
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Run rebuilding — the one place the timing rule lives.
───────────────────────────────────────────────────────────────────────────────────────*/

interface RebuiltRuns {
  readonly runs: readonly TextRun[];
  readonly interpolated: readonly RunId[];
  /** true when at least one run kept its identity and content unchanged. */
  readonly reusedAll: boolean;
}

/**
 * Groups spliced characters back into runs by their source-run provenance.
 *
 * A surviving run keeps its identity, style, timing and confidence VERBATIM. A run split
 * into fragments keeps its id on the FIRST fragment (so the diff aligns) and mints fresh ids
 * for the rest. A group of INSERTED characters becomes a new run that inherits the
 * neighbouring style and is timed only when both neighbours are timed and the gap between
 * them is positive — otherwise its time stays null, because fabricating a word timing is
 * worse than admitting there is not one.
 *
 * `sourceRuns` is indexed by `charToRun[i]`; both are caller-supplied so this works for a
 * single line and for a whole cue (where a deleted '\n' merges two lines' runs).
 */
function rebuildRuns(
  sourceRuns: readonly TextRun[],
  charToRun: readonly number[],
  applied: Applied,
  alloc: IdAllocator,
  usedOriginalId: Set<number>,
): RebuiltRuns {
  const groups: { run: number; text: string }[] = [];
  let current: { run: number; text: string } | null = null;
  for (let i = 0; i < applied.text.length; i++) {
    const src = applied.from[i] as number;
    const runIdx = src === INSERTED ? INSERTED : (charToRun[src] as number);
    if (current !== null && current.run === runIdx) current.text += applied.text[i] as string;
    else {
      if (current !== null) groups.push(current);
      current = { run: runIdx, text: applied.text[i] as string };
    }
  }
  if (current !== null) groups.push(current);

  const out: TextRun[] = [];
  const interpolated: RunId[] = [];
  let reusedAll = true;

  for (let g = 0; g < groups.length; g++) {
    const group = groups[g] as { run: number; text: string };
    if (group.run !== INSERTED) {
      const src = sourceRuns[group.run] as TextRun;
      const first = !usedOriginalId.has(group.run);
      usedOriginalId.add(group.run);
      if (first && src.text === group.text) {
        out.push(src); // BY REFERENCE — structural sharing is contract, not optimisation
      } else {
        reusedAll = false;
        out.push({ ...src, id: first ? src.id : alloc.run(), text: group.text });
      }
      continue;
    }
    reusedAll = false;
    const prevRun = out.length > 0 ? (out[out.length - 1] as TextRun) : null;
    const nextGroup = groups[g + 1];
    const nextRun =
      nextGroup && nextGroup.run !== INSERTED ? (sourceRuns[nextGroup.run] as TextRun) : null;
    const donor = prevRun ?? nextRun;
    const time = interpolateBetween(prevRun, nextRun);
    const id = alloc.run();
    if (time !== null) interpolated.push(id);
    out.push({
      id,
      kind: 'text',
      text: group.text,
      time,
      timeKind: time === null ? null : (prevRun?.timeKind ?? nextRun?.timeKind ?? 'word'),
      timeOrigin: time === null ? null : 'interpolated',
      confidence: null,
      startsWord: true,
      style: donor?.style ?? {},
      annotation: null,
      drawing: null,
      sourceOffset: null,
    });
  }
  return { runs: out, interpolated, reusedAll };
}

function interpolateBetween(
  prev: TextRun | null,
  next: TextRun | null,
): { start: Ticks; end: Ticks } | null {
  if (!prev?.time || !next?.time) return null;
  const start = prev.time.end as number;
  const end = next.time.start as number;
  return end > start ? { start: T.ticks(start), end: T.ticks(end) } : null;
}

/*───────────────────────────────────────────────────────────────────────────────────────
  spliceLine / spliceCue
───────────────────────────────────────────────────────────────────────────────────────*/

export interface SpliceReport {
  /**
   * Runs created by the splice that received a synthesized timing. The caller emits loss
   * 'timing-interpolated' for these. The contract's `spliceLine`/`spliceCue` return only the
   * value, so this detail channel sits ALONGSIDE them rather than changing their signature.
   */
  readonly interpolatedRuns: readonly RunId[];
  readonly changed: boolean;
}

export interface SpliceLineResult extends SpliceReport {
  readonly line: TextLine;
}

export function spliceLine(
  line: TextLine,
  edits: readonly TextEdit[],
  alloc: IdAllocator,
): TextLine {
  return spliceLineDetailed(line, edits, alloc).line;
}

export function spliceLineDetailed(
  line: TextLine,
  edits: readonly TextEdit[],
  alloc: IdAllocator,
): SpliceLineResult {
  const flat = flattenLine(line);
  const applied = applyEdits(flat.text, edits);
  if (!applied.changed) return { line, interpolatedRuns: [], changed: false };

  const built = rebuildRuns(line.runs, flat.runIndex, applied, alloc, new Set());
  const drawings = line.runs.filter((r) => r.kind === 'drawing');
  const runs = drawings.length === 0 ? built.runs : [...built.runs, ...drawings];
  return { line: { ...line, runs }, interpolatedRuns: built.interpolated, changed: true };
}

export interface SpliceCueResult extends SpliceReport {
  readonly cue: Cue;
}

export function spliceCue(cue: Cue, edits: readonly TextEdit[], alloc: IdAllocator): Cue {
  return spliceCueDetailed(cue, edits, alloc).cue;
}

export function spliceCueDetailed(
  cue: Cue,
  edits: readonly TextEdit[],
  alloc: IdAllocator,
): SpliceCueResult {
  const flat = flattenCueInternal(cue);
  const applied = applyEdits(flat.text, edits);
  if (!applied.changed) return { cue, interpolatedRuns: [], changed: false };

  interface Segment {
    text: string;
    from: number[];
    sourceLine: number;
    createdByInsert: boolean;
  }
  const segments: Segment[] = [];
  let seg: Segment = { text: '', from: [], sourceLine: 0, createdByInsert: false };
  for (let i = 0; i < applied.text.length; i++) {
    const ch = applied.text[i] as string;
    const src = applied.from[i] as number;
    if (ch === '\n') {
      segments.push(seg);
      const inserted = src === INSERTED;
      seg = {
        text: '',
        from: [],
        sourceLine: inserted ? -1 : (flat.lineIndex[src] as number),
        createdByInsert: inserted,
      };
      continue;
    }
    seg.text += ch;
    seg.from.push(src);
  }
  segments.push(seg);

  // One id-reuse ledger for the WHOLE cue: a run split across two output lines must not
  // claim its original id twice.
  const usedOriginalId = new Set<number>();
  const interpolated: RunId[] = [];
  const lines: TextLine[] = [];

  for (const s of segments) {
    const charToRun: number[] = [];
    const localFrom: number[] = [];
    const sourceLines = new Set<number>();
    for (const f of s.from) {
      if (f === INSERTED) {
        localFrom.push(INSERTED);
      } else {
        localFrom.push(charToRun.length);
        charToRun.push(flat.globalRun[f] as number);
        sourceLines.add(flat.lineIndex[f] as number);
      }
    }
    // Which source line's PROPERTIES (breakBefore, align, attribution) does this line
    // inherit? The first one it drew a character from; the recorded one when it drew none.
    const firstSourceLine =
      sourceLines.size > 0 ? Math.min(...sourceLines) : s.sourceLine >= 0 ? s.sourceLine : 0;
    const srcLine = cue.lines[firstSourceLine] ?? (cue.lines[0] as TextLine);

    const built = rebuildRuns(
      flat.runs,
      charToRun,
      { text: s.text, from: localFrom, changed: true },
      alloc,
      usedOriginalId,
    );
    interpolated.push(...built.interpolated);

    // Drawing runs carry no characters, so they have no provenance. Re-attach every drawing
    // from every source line this segment drew from — a merged line keeps both signs.
    const drawings: TextRun[] = [];
    for (const li of sourceLines.size > 0
      ? [...sourceLines].sort((a, b) => a - b)
      : [firstSourceLine]) {
      for (const r of (cue.lines[li] as TextLine | undefined)?.runs ?? []) {
        if (r.kind === 'drawing') drawings.push(r);
      }
    }
    const runs = drawings.length === 0 ? built.runs : [...built.runs, ...drawings];

    // A line emptied by an edit is DELETED, never left as a blank first line.
    if (runs.length === 0) continue;
    lines.push({
      ...srcLine,
      runs,
      breakBefore: s.createdByInsert ? 'reflowed' : srcLine.breakBefore,
    });
  }

  // lines.length >= 1 is a document invariant. A cue whose text was entirely deleted keeps
  // one empty line; plainText === '' is the emptiness signal, and dropping the cue is the
  // CALLER's decision (dropCueIfEmpty), never this primitive's.
  const nonEmpty = lines.length > 0 ? lines : [{ ...(cue.lines[0] as TextLine), runs: [] }];
  const finalLines = nonEmpty.map((l, i) =>
    i === 0 && l.breakBefore !== 'none' ? { ...l, breakBefore: 'none' as const } : l,
  );

  return {
    cue: {
      ...cue,
      rev: cue.rev + 1,
      lines: finalLines,
      flags: recomputeTextFlags(cue, finalLines),
    },
    interpolatedRuns: interpolated,
    changed: true,
  };
}

/**
 * `hasFragileTags` and `hasRelativeTimes` are DERIVED from the runs, so a splice that drops
 * the last animated run must clear them and a splice that keeps a karaoke run must keep
 * `hasRelativeTimes` set. Op authors do not hand-roll this; it is applied mechanically.
 */
export function recomputeTextFlags(cue: Cue, lines: readonly TextLine[]): Cue['flags'] {
  let fragile = false;
  // \move and \fad are MODELLED, not fragile — but they are still duration-encoded in the
  // raw text column, so they set hasRelativeTimes.
  let relative = cue.fade !== null || cue.position?.move != null;
  for (const line of lines) {
    for (const run of line.runs) {
      if (run.timeKind !== null) relative = true;
      const anims = run.style.animations;
      if (anims && anims.length > 0) {
        fragile = true;
        relative = true;
      }
      for (const tag of run.style.unknownTags ?? []) {
        if (tag.argsUnclassified) fragile = true;
        if (tagCarriesTime(tag)) relative = true;
      }
    }
  }
  return { ...cue.flags, hasFragileTags: fragile, hasRelativeTimes: relative };
}

interface TagLike {
  readonly args: readonly { readonly kind: string; readonly value?: unknown }[];
}
function tagCarriesTime(tag: TagLike): boolean {
  for (const arg of tag.args) {
    if (arg.kind === 'offset' || arg.kind === 'duration') return true;
    if (arg.kind === 'nested') {
      for (const n of (arg.value as readonly TagLike[] | undefined) ?? [])
        if (tagCarriesTime(n)) return true;
    }
  }
  return false;
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Conveniences over spliceCue. Same frozen timing rule, by construction.
───────────────────────────────────────────────────────────────────────────────────────*/

export function splitLineAt(
  line: TextLine,
  charIndex: number,
  alloc: IdAllocator,
): readonly [TextLine, TextLine] {
  const flat = flattenLine(line);
  const at = Math.max(0, Math.min(flat.text.length, Math.trunc(charIndex)));

  // One shared id ledger across both halves: a run cut in two keeps its id on the head and
  // gets a fresh one on the tail, exactly as a mid-run splice would.
  const used = new Set<number>();
  const head = sliceOf(line, flat, 0, at, alloc, used, line.breakBefore);
  const tail = sliceOf(line, flat, at, flat.text.length, alloc, used, 'reflowed');
  return [head, tail];
}

function sliceOf(
  line: TextLine,
  flat: FlatLine,
  from: number,
  to: number,
  alloc: IdAllocator,
  used: Set<number>,
  breakBefore: TextLine['breakBefore'],
): TextLine {
  const text = flat.text.slice(from, to);
  const charToRun = flat.runIndex.slice(from, to);
  const built = rebuildRuns(
    line.runs,
    charToRun,
    { text, from: identityFrom(text.length), changed: true },
    alloc,
    used,
  );
  return { ...line, runs: built.runs, breakBefore };
}

/**
 * Joins lines with a joiner string.
 *
 * The joiner is APPENDED TO THE LAST TEXT RUN of the preceding line rather than inserted as
 * a run of its own. Inserting an untimed run between two timed ones would turn a fully
 * word-timed cue into `wordTimingCoverage: 'partial'` and silently disable the karaoke and
 * Enhanced-LRC export paths — the product's differentiator, destroyed by a space.
 */
export function joinLines(
  lines: readonly TextLine[],
  joiner: string,
  alloc: IdAllocator,
): TextLine {
  if (lines.length === 0)
    return { runs: [], breakBefore: 'none', align: null, trackId: null, inputId: null };
  const first = lines[0] as TextLine;
  if (lines.length === 1) return first;

  const runs: TextRun[] = [];
  const seen = new Set<RunId>();
  for (let l = 0; l < lines.length; l++) {
    const line = lines[l] as TextLine;
    if (l > 0 && joiner !== '') {
      const lastText = lastTextRunIndex(runs);
      if (lastText >= 0) {
        const r = runs[lastText] as TextRun;
        runs[lastText] = { ...r, text: r.text + joiner };
      } else {
        runs.push(makeJoinerRun(alloc.run(), joiner));
      }
    }
    for (const run of line.runs) {
      if (seen.has(run.id)) runs.push({ ...run, id: alloc.run() });
      else {
        seen.add(run.id);
        runs.push(run);
      }
    }
  }
  return { ...first, runs };
}

function makeJoinerRun(id: RunId, joiner: string): TextRun {
  return {
    id,
    kind: 'text',
    text: joiner,
    time: null,
    timeKind: null,
    timeOrigin: null,
    confidence: null,
    startsWord: false,
    style: {},
    annotation: null,
    drawing: null,
    sourceOffset: null,
  };
}

function lastTextRunIndex(runs: readonly TextRun[]): number {
  for (let i = runs.length - 1; i >= 0; i--) if ((runs[i] as TextRun).kind === 'text') return i;
  return -1;
}

/*───────────────────────────────────────────────────────────────────────────────────────
  ensureWordRuns
───────────────────────────────────────────────────────────────────────────────────────*/

/**
 * Splits COALESCED runs into word runs on demand, preserving style and timing. Idempotent.
 *
 * A run that already carries a `time` is NOT split: a karaoke syllable is SUB-word, and
 * splitting it would either fabricate word times or duplicate one syllable's time onto two
 * words. That is the frozen timing rule applied to this operation, and it is why the ASR
 * path — which emits word runs directly — needs nothing from this function.
 */
export function ensureWordRuns(cue: Cue, alloc: IdAllocator): Cue {
  if (cue.granularity === 'word') return cue;
  let changed = false;
  const lines = cue.lines.map((line) => {
    const runs: TextRun[] = [];
    let lineChanged = false;
    for (const run of line.runs) {
      if (run.kind === 'drawing' || run.time !== null || run.text === '') {
        runs.push(run);
        continue;
      }
      const pieces = splitIntoWords(run.text);
      if (pieces.length <= 1) {
        runs.push(run);
        continue;
      }
      lineChanged = true;
      changed = true;
      for (let i = 0; i < pieces.length; i++) {
        runs.push({
          ...run,
          id: i === 0 ? run.id : alloc.run(),
          text: pieces[i] as string,
          startsWord: true,
        });
      }
    }
    return lineChanged ? { ...line, runs } : line;
  });
  if (!changed) return { ...cue, granularity: 'word' };
  return { ...cue, rev: cue.rev + 1, lines, granularity: 'word' };
}

/**
 * Word boundaries. A word begins where a whitespace run gives way to non-whitespace, and
 * every CJK character is its own word.
 *
 * Leading whitespace attaches to the FOLLOWING word — the convention faster-whisper uses
 * (' So', ' today'), so a file path and an ASR path produce comparable run text and a
 * karaoke tag placed at a run boundary contains its own leading space, which is what players
 * expect. See contracts/transcript-wire.md § "Leading spaces, downstream".
 */
export function splitIntoWords(text: string): readonly string[] {
  if (text === '') return [];
  const cps = [...text];
  const n = cps.length;
  const isSpace = (i: number): boolean => /\s/u.test(cps[i] as string);
  const isMark = (i: number): boolean => /[\p{Mn}\p{Me}]/u.test(cps[i] as string);
  const isCjk = (i: number): boolean => isCjkCodePoint((cps[i] as string).codePointAt(0) as number);

  const bounds: number[] = [0];
  for (let i = 1; i < n; i++) {
    if (isMark(i)) continue;
    if (isSpace(i) && !isSpace(i - 1)) {
      // Start of a whitespace run — a boundary only when a word actually follows it, so a
      // trailing space never produces an empty final word.
      let j = i;
      while (j < n && isSpace(j)) j++;
      if (j < n) bounds.push(i);
      continue;
    }
    if (!isSpace(i) && !isSpace(i - 1) && (isCjk(i) || isCjk(i - 1))) bounds.push(i);
  }
  bounds.push(n);

  const out: string[] = [];
  for (let b = 0; b < bounds.length - 1; b++) {
    const piece = cps.slice(bounds[b] as number, bounds[b + 1] as number).join('');
    if (piece !== '') out.push(piece);
  }
  return out;
}
